import React, { useEffect, useState, useMemo, useRef } from 'react';
import { API_BASE } from '../config/api';
import '../styles/Common.css';
const DEFAULT_SEAM_WIDTH = {
  manual: '15',
  motor: '20',
};
const DEFAULT_SEAM_HEIGHT = {
  curtain: '45',
  roll: '0',
};

const toNumber = (value) => Number(value) || 0;

const formatMm = (value, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }
  if (Number.isInteger(value)) {
    return `${value} mm`;
  }
  return `${value.toFixed(digits)} mm`;
};

const calculateBlindBySpec = ({ headRailHeight, stepGap, productHeight, stepCount, bottomBarPosition }) => {
  const A = toNumber(headRailHeight);
  const C = toNumber(stepGap);
  const H = toNumber(productHeight);
  const Z = toNumber(stepCount);
  const K = bottomBarPosition;

  if (!A || !H || Z <= 0) {
    return { averageFabricHeight: null, positions: [] };
  }

  const denominator = (2 * Z) + K;
  if (!denominator) {
    return { averageFabricHeight: null, positions: [] };
  }

  const averageFabricHeight = (
    H
    - A
    - (Z * (Z - 1) * C)
    - (Z * C * K)
  ) / denominator;

  if (!Number.isFinite(averageFabricHeight) || averageFabricHeight <= 0) {
    return { averageFabricHeight: null, positions: [] };
  }

  const stepCountForPositions = Math.floor(Z);
  if (stepCountForPositions <= 0) {
    return { averageFabricHeight, positions: [] };
  }

  const positions = [];
  let prevPosition = A + (2 * averageFabricHeight);
  positions.push(prevPosition);

  for (let n = 2; n <= stepCountForPositions; n += 1) {
    const nextPosition = prevPosition + (2 * averageFabricHeight) + (2 * (n - 1) * C);
    positions.push(nextPosition);
    prevPosition = nextPosition;
  }

  return { averageFabricHeight, positions };
};

const RomanCalcPage = () => {
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [fabricType, setFabricType] = useState('');
  const [systemType, setSystemType] = useState('');
  const [seamWidth, setSeamWidth] = useState('');
  const [seamHeight, setSeamHeight] = useState('');
  const [stackGap, setStackGap] = useState('');
  const [stepFinish, setStepFinish] = useState('bottom');
  const [headrails, setHeadrails] = useState([]);
  const [middles, setMiddles] = useState([]);
  const [bottoms, setBottoms] = useState([]);
  const [headId, setHeadId] = useState('');
  const [middleId, setMiddleId] = useState('');
  const [middleCount, setMiddleCount] = useState(0);
  const [bottomId, setBottomId] = useState('');
  const [bottomType, setBottomType] = useState('');
  const [loading, setLoading] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const resultStickyRef = useRef(null);
  const lastLogRef = useRef('');

  const selectedHead = useMemo(
    () => headrails.find((item) => String(item.id) === String(headId)),
    [headrails, headId]
  );
  const selectedMiddle = useMemo(
    () => middles.find((item) => String(item.id) === String(middleId)),
    [middles, middleId]
  );
  const selectedBottom = useMemo(
    () => bottoms.find((item) => String(item.id) === String(bottomId)),
    [bottoms, bottomId]
  );

  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoading(true);
        const [hRes, mRes, bRes] = await Promise.all([
          fetch(`${API_BASE}/api/rm_headrail?include_inactive=1`),
          fetch(`${API_BASE}/api/rm_middle?include_inactive=1`),
          fetch(`${API_BASE}/api/rm_bottombar?include_inactive=1`),
        ]);
        const h = await hRes.json();
        const m = await mRes.json();
        const b = await bRes.json();
        setHeadrails(h);
        setMiddles(m);
        setBottoms(b);
      } catch (error) {
        console.error('로만 옵션 조회 실패', error);
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  useEffect(() => {
    if (DEFAULT_SEAM_WIDTH[systemType]) {
      setSeamWidth(DEFAULT_SEAM_WIDTH[systemType]);
    }
  }, [systemType]);

  useEffect(() => {
    if (DEFAULT_SEAM_HEIGHT[fabricType]) {
      setSeamHeight(DEFAULT_SEAM_HEIGHT[fabricType]);
    }
  }, [fabricType]);

  useEffect(() => {
    if (fabricType === 'roll') {
      setBottomType('welding');
    }
  }, [fabricType]);

  useEffect(() => {
    const updateStickyState = () => {
      const el = resultStickyRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const stuck = Math.abs(rect.bottom - window.innerHeight) <= 1;
      setIsStuck(stuck);
    };

    updateStickyState();
    window.addEventListener('scroll', updateStickyState, { passive: true });
    window.addEventListener('resize', updateStickyState);
    return () => {
      window.removeEventListener('scroll', updateStickyState);
      window.removeEventListener('resize', updateStickyState);
    };
  }, []);

  const calcWidth = useMemo(() => {
    return toNumber(width) + toNumber(seamWidth);
  }, [width, seamWidth]);

  const calcHeight = useMemo(() => {
    const h = toNumber(height);
    const sh = toNumber(seamHeight);
    const headH = toNumber(selectedHead?.height);
    const middleH = toNumber(selectedMiddle?.height);
    const bottomH = toNumber(selectedBottom?.height);
    const weldingExtra = bottomType === 'welding' ? 10 : 0;

    return (
      headH +
      middleH * toNumber(middleCount) +
      bottomH +
      h +
      sh +
      weldingExtra
    );
  }, [
    height,
    seamHeight,
    selectedHead,
    selectedMiddle,
    selectedBottom,
    middleCount,
    bottomType
  ]);

  const blindCalculation = useMemo(() => {
    const mappedInput = {
      headRailHeight: toNumber(selectedHead?.height),
      stepGap: toNumber(stackGap),
      productHeight: toNumber(height),
      stepCount: toNumber(middleCount),
      bottomBarPosition: stepFinish === 'bottom' ? 1 : 0,
    };

    const result = calculateBlindBySpec(mappedInput);

    const logPayload = {
      mappedInput: {
        headRailHeight_A: mappedInput.headRailHeight,
        stepGap_C: mappedInput.stepGap,
        productHeight_H: mappedInput.productHeight,
        stepCount_Z: mappedInput.stepCount,
        bottomBarPosition_K: mappedInput.bottomBarPosition,
      },
      averageFabricHeight_B: result.averageFabricHeight,
      positions: result.positions,
    };

    const logKey = JSON.stringify(logPayload);
    if (logKey !== lastLogRef.current) {
      lastLogRef.current = logKey;
      console.log('[로만 스펙 계산]', logPayload);
    }

    return result;
  }, [selectedHead, stackGap, middleCount, stepFinish, height]);

  const averageFabricHeight = blindCalculation.averageFabricHeight;
  const foldPositions = blindCalculation.positions;
  const foldSplitIndex = Math.ceil(foldPositions.length / 2);
  const pairedFoldRows = Array.from({ length: foldSplitIndex }, (_, rowIndex) => {
    const leftIndex = rowIndex;
    const rightIndex = rowIndex + foldSplitIndex;

    return {
      left: leftIndex < foldPositions.length ? { order: leftIndex + 1, pos: foldPositions[leftIndex] } : null,
      right: rightIndex < foldPositions.length ? { order: rightIndex + 1, pos: foldPositions[rightIndex] } : null,
    };
  });

  const systemLabel =
    systemType === 'manual' ? '수동' : systemType === 'motor' ? '전동' : '-';
  const fabricLabel =
    fabricType === 'roll' ? '롤' : fabricType === 'curtain' ? '커튼' : '-';

  return (
    <div className="common-container roman-settings roman-calc">
      <div className="roman-calc-inner">
        <h2 className="page-title">로만 쉐이드 사이즈 계산기</h2>

        <div className="roman-card">
          {loading && <p>옵션 불러오는 중…</p>}

        <div className="roman-inline-sections">
          <div className="roman-inline-section">
            <h3 className="section-title">원단 종류</h3>
            <div className="choice-cards choice-cards-inline">
              <label className={`choice-card ${fabricType === 'roll' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="fabricType"
                  value="roll"
                  checked={fabricType === 'roll'}
                  onChange={(e) => setFabricType(e.target.value)}
                />
                <span>롤</span>
              </label>
              <label className={`choice-card ${fabricType === 'curtain' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="fabricType"
                  value="curtain"
                  checked={fabricType === 'curtain'}
                  onChange={(e) => setFabricType(e.target.value)}
                />
                <span>커튼</span>
              </label>
            </div>
          </div>

          <div className="roman-inline-section">
            <h3 className="section-title">시스템</h3>
            <div className="choice-cards choice-cards-inline">
              <label className={`choice-card ${systemType === 'manual' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="systemType"
                  value="manual"
                  checked={systemType === 'manual'}
                  onChange={(e) => setSystemType(e.target.value)}
                />
                <span>수동</span>
              </label>
              <label className={`choice-card ${systemType === 'motor' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="systemType"
                  value="motor"
                  checked={systemType === 'motor'}
                  onChange={(e) => setSystemType(e.target.value)}
                />
                <span>전동</span>
              </label>
            </div>
          </div>
        </div>

        <div className="roman-form">
          <div className="form-field">
            <label>계단 간격 (mm)</label>
            <input
              type="number"
              value={stackGap}
              onChange={(e) => setStackGap(e.target.value)}
              placeholder="예: 50"
            />
          </div>
        </div>

        <h3 className="section-title">마무리</h3>
        <div className="choice-cards">
          <label className={`choice-card ${stepFinish === 'bottom' ? 'active' : ''}`}>
            <input
              type="radio"
              name="stepFinish"
              value="bottom"
              checked={stepFinish === 'bottom'}
              onChange={(e) => setStepFinish(e.target.value)}
            />
            <span>하단</span>
          </label>
          <label className={`choice-card ${stepFinish === 'top' ? 'active' : ''}`}>
            <input
              type="radio"
              name="stepFinish"
              value="top"
              checked={stepFinish === 'top'}
              onChange={(e) => setStepFinish(e.target.value)}
            />
            <span>상단</span>
          </label>
        </div>

        {/* ===== 완료창 사이즈 ===== */}
        <h3 className="section-title">완성창 사이즈</h3>
        <div className="roman-form">
          <div className="form-field">
            <label>가로 (mm)</label>
            <input
              type="number"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              placeholder="예: 2000"
            />
          </div>

          <div className="form-field">
            <label>세로 (mm)</label>
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="예: 1500"
            />
          </div>
        </div>

        {/* ===== 시접 ===== */}
        <h3 className="section-title">시접</h3>
        <div className="roman-form">
          <div className="form-field">
            <label>가로 시접 (mm)</label>
            <input
              type="number"
              value={seamWidth}
              onChange={(e) => setSeamWidth(e.target.value)}
              placeholder="예: 30"
            />
          </div>

          <div className="form-field">
            <label>세로 시접 (mm)</label>
            <input
              type="number"
              value={seamHeight}
              onChange={(e) => setSeamHeight(e.target.value)}
              placeholder="예: 30"
            />
          </div>
        </div>

        {/* ===== 헤드레일 ===== */}
        <h3 className="section-title">헤드레일</h3>
        <select value={headId} onChange={(e) => setHeadId(e.target.value)}>
          <option value="">선택하세요</option>
          {headrails.map(h => (
            <option key={h.id} value={h.id}>
              {h.name} ({h.height ?? '-'}mm)
            </option>
          ))}
        </select>

        {/* ===== 미들 ===== */}
        <h3 className="section-title">미들</h3>
        <div className="roman-form">
          <div className="form-field">
            <label>미들 종류</label>
            <select
              className="roman-select-compact"
              value={middleId}
              onChange={(e) => setMiddleId(e.target.value)}
            >
              <option value="">선택하세요</option>
              {middles.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.height ?? '-'}mm)
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>미들 개수</label>
            <input
              type="range"
              min="0"
              max="20"
              step="1"
              value={middleCount}
              onChange={(e) => setMiddleCount(Number(e.target.value))}
            />
            <div>{middleCount} 개</div>
          </div>
        </div>

        <h3 className="section-title">하단 옵션</h3>
        <div className="roman-form">
          <div className="form-field">
            <label>하단바</label>
            <select value={bottomId} onChange={(e) => setBottomId(e.target.value)}>
              <option value="">선택하세요</option>
              {bottoms.map(b => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.height ?? '-'}mm)
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>하단 타입</label>
            <div className="choice-cards choice-cards-inline">
              <label className={`choice-card ${bottomType === 'welding' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="bottomType"
                  value="welding"
                  checked={bottomType === 'welding'}
                  onChange={(e) => setBottomType(e.target.value)}
                />
                <span>웰딩</span>
              </label>
              <label
                className={`choice-card ${bottomType === 'sewing' ? 'active' : ''} ${
                  fabricType === 'roll' ? 'is-disabled' : ''
                }`}
              >
                <input
                  type="radio"
                  name="bottomType"
                  value="sewing"
                  checked={bottomType === 'sewing'}
                  onChange={(e) => setBottomType(e.target.value)}
                  disabled={fabricType === 'roll'}
                />
                <span>미싱</span>
              </label>
            </div>
          </div>
        </div>

        </div>
      </div>

      {/* ===== 계산 결과 ===== */}
      <h3 className="section-title roman-result-title">📏 계산 결과</h3>
      <div
        ref={resultStickyRef}
        className={`roman-result-sticky ${isStuck ? 'is-stuck' : ''}`}
      >
        <div className="roman-result-inner print-area">
          <div className="print-summary print-only">
            <div className="roman-result-subtitle">선택 요약</div>
            <table className="print-summary-table">
              <tbody>
                <tr>
                  <th>완성창 사이즈</th>
                  <td>
                    {width ? `${width} mm` : '-'} / {height ? `${height} mm` : '-'}
                  </td>
                </tr>
                <tr>
                  <th>원단 종류</th>
                  <td>{fabricLabel}</td>
                </tr>
                <tr>
                  <th>시스템</th>
                  <td>{systemLabel}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="roman-result-top">
            <div className="roman-result-card roman-result-combined">
              <div className={`roman-result-layout ${foldPositions.length > 0 ? '' : 'no-folds'}`}>
                <div className="roman-result-summary">
                  <div className="roman-result-row roman-result-size-line">
                    <span>제작 사이즈</span>
                    <strong>
                      {formatMm(calcWidth, 0)} / {formatMm(calcHeight, 0)}
                    </strong>
                  </div>
                  <div className="roman-result-row roman-result-size-line">
                    <span>평균 원단 높이 (B)</span>
                    <strong>
                      {averageFabricHeight === null ? '-' : formatMm(averageFabricHeight, 2)}
                    </strong>
                  </div>
                </div>

                {foldPositions.length > 0 && (
                  <div className="roman-result-folds">
                    <div className="roman-result-subtitle">접는부위</div>
                    <table className="roman-result-table roman-result-table-paired">
                      <thead>
                        <tr>
                          <th>순서</th>
                          <th>위치</th>
                          <th>순서</th>
                          <th>위치</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pairedFoldRows.map((row, idx) => (
                          <tr key={`paired-fold-row-${idx}`}>
                            <td>{row.left ? `${row.left.order}번째` : '-'}</td>
                            <td>{row.left ? formatMm(row.left.pos, 1) : '-'}</td>
                            <td>{row.right ? `${row.right.order}번째` : '-'}</td>
                            <td>{row.right ? formatMm(row.right.pos, 1) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="roman-print-row no-print">
            <button
              type="button"
              className="roman-print-button"
              onClick={() => window.print()}
            >
              출력
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RomanCalcPage;
